import styled, {keyframes} from "styled-components";
import React from "react";
import {useDroppable} from "@dnd-kit/core";
import {DragItemType} from "../types";
import {BaseItem} from "../ToolsPanel/base_items/BaseItem";
import {Bomb} from "../ToolsPanel/base_items/Bomb";

const opacityAnimation = keyframes`
    0% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
`;

const StyledCell = styled.div<{ size: number }>`
    flex: 1;
    height: ${({size}) => size}px;
    border: 1px solid rgba(133, 116, 208, 0.5);
`;

const CellHint = styled.div<{isHinted: boolean}>`
    filter: ${({isHinted}) => isHinted ? 'hue-rotate(45deg)' : 'none'};
    background: ${({isHinted}) => isHinted ? 'radial-gradient(circle, #478ebf 0%, #339EBD 20%, #89F9F1 100%)' : 'initial'};
    animation: ${opacityAnimation} 2s infinite ease-in-out;
    width: 100%;
    height: 100%;
`;

export type TOptions = {
    elementPosition: {
        rowIndex: number;
        columnIndex: number;
    };
};

interface Props {
    elementPosition: {
        rowIndex: number;
        columnIndex: number;
    }
    dragItemType?: DragItemType;
    size: number;
    isHinted: boolean;
}

export const Cell = ({elementPosition, dragItemType, size, isHinted}: Props) => {
    const {setNodeRef} = useDroppable({
        id: `${elementPosition.rowIndex}-${elementPosition.columnIndex}`,
    });

    switch (dragItemType?.typeOfItem) {
        case "BOMB":
            return <Bomb movesLeft={dragItemType?.bombMovesLeft} size={size} isDisappearance={dragItemType.isDisappearance} />;
        case "SINGLE":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;

        case "DOUBLE_COL":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "TRIPLE_COL":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "FOUR_COL":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "FIVE_COL":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;

        case "DOUBLE_ROW":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "TRIPLE_ROW":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "FOUR_ROW":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "FIVE_ROW":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;

        case "DOUBLE_SQUARE":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>;
        case "TRIPLE_SQUARE":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>

        case "MINI_ANGLE_LEFT_TOP":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "MINI_ANGLE_RIGHT_TOP":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "MINI_ANGLE_RIGHT_BOTTOM":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "MINI_ANGLE_LEFT_BOTTOM":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>

        case "ANGLE_LEFT_TOP":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "ANGLE_LEFT_BOTTOM":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "ANGLE_RIGHT_BOTTOM":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        case "ANGLE_RIGHT_TOP":
            return <BaseItem typeOfItem={dragItemType?.typeOfItem} withAnimation size={size}
                             isDisappearance={dragItemType.isDisappearance} type={dragItemType.type}/>
        default:
            return <StyledCell size={size} ref={setNodeRef}>
                {isHinted && <CellHint isHinted={isHinted} />}
            </StyledCell>;
    }
}
